import { i18n } from '../i18n/index.ts';
import { formatBytes } from './file-size.ts';

export type ContentFilter = 'all' | 'slide' | 'video' | 'assessment';

export const CONTENT_TYPES_BY_FILTER: Record<Exclude<ContentFilter, 'all'>, string[]> = {
  slide: ['pdf', 'zip-html', 'lesson'],
  video: ['video', 'vimeo'],
  assessment: ['test', 'matrix', 'reading-test', 'listening-test'],
};

export function normalizeContentType(type: string | null): string {
  return String(type || '').trim().toLowerCase();
}

export function contentFilterMatches(filter: ContentFilter, type: string | null): boolean {
  if (filter === 'all') return true;
  return CONTENT_TYPES_BY_FILTER[filter].indexOf(normalizeContentType(type)) >= 0;
}

export type ContentKind = 'slide' | 'video' | 'assessment' | 'other';

export function contentKind(type: string | null): ContentKind {
  const normalized = normalizeContentType(type);
  if (CONTENT_TYPES_BY_FILTER.slide.indexOf(normalized) >= 0) return 'slide';
  if (CONTENT_TYPES_BY_FILTER.video.indexOf(normalized) >= 0) return 'video';
  if (CONTENT_TYPES_BY_FILTER.assessment.indexOf(normalized) >= 0) return 'assessment';
  return 'other';
}

// Labels for the other IZ unit types; an unknown type is "Other", never a raw code
function otherTypeLabel(normalized: string): string {
  switch (normalized) {
    case 'homework':
      return i18n.t('errors:contentType.homework');
    case 'exercise':
      return i18n.t('errors:contentType.exercise');
    case 'lesson':
      return i18n.t('errors:contentType.lesson');
    case 'matrix':
      return i18n.t('errors:contentType.matrix');
    default:
      return i18n.t('errors:contentType.other');
  }
}

/* short form for chips and tags */
export function contentTypeLabel(type: string | null): string {
  const kind = contentKind(type);
  if (kind === 'slide') return i18n.t('errors:contentType.slide');
  if (kind === 'video') return i18n.t('errors:contentType.video');
  if (kind === 'assessment') return i18n.t('errors:contentType.assessmentShort');
  return otherTypeLabel(normalizeContentType(type));
}

/* long form for the kho bài unit subtitle */
export function contentKindLabel(type: string | null): string {
  const kind = contentKind(type);
  if (kind === 'slide') return i18n.t('errors:contentType.slide');
  if (kind === 'video') return i18n.t('errors:contentType.video');
  if (kind === 'assessment') return i18n.t('errors:contentType.assessment');
  return contentTypeLabel(type);
}

/** The one metadata line formatter - same separator, same order, every card. */
export function contentMetaLine(parts: Array<string | null | undefined>): string {
  return parts.filter((part) => Boolean(part)).join(' • ');
}

/** Strapi reports upload sizes in kilobytes; formatBytes owns the scaling. */
export function formatFileSize(kilobytes: number): string {
  return formatBytes(kilobytes * 1024);
}

/** Source titles carry an inconsistent "6." prefix - the tree renders its own ordinal instead. */
export function stripLeadingOrdinal(title: string): string {
  const stripped = String(title || '').replace(/^\s*\d{1,3}\s*[.)]\s*/, '').trim();
  return stripped || String(title || '').trim();
}
